import React from 'react';
import WeekCheckboxes from '../MonthlyCalendar/WeekCheckboxes';
import GET_ATL_CALENDAR from '../../queries/getATLCalendarQuery';
import ADD_UNAVAILABLE_PERIOD from '../../queries/UnavailablePeriods/AddUnavailablePeriodMutation';
import DELETE_UNAVAILABLE_PERIOD from '../../queries/UnavailablePeriods/deleteUnavailablePeriodMutation';
import { useMutation } from '@apollo/client';
import SkeletonWrapper from '@shared/components/skeleton/SkeletonWrapper';
import ApolloError from '@shared/components/ApolloError';
import CalendarMapper from '../../mappers/CalendarMapper';
import { getWeekCheckBoxes } from '../../services/calendarWeeksService';
import MonthlyDatePicker from '../MonthlyCalendar/MonthlyDatePicker';
import MonthlyCalendar from '../MonthlyCalendar/MonthlyCalendar';
import CalendarEventMapper from '../../mappers/CalendarEventMapper';
import ViewSwitch from '../shared/components/ViewSwitch.js';
import {
  addPeriodToCache,
  removePeriodFromCache
} from '../../services/updateUnavailablePeriodsCache';
import GET_UNAVAILABLE_PERIODS from '../../queries/UnavailablePeriods/getUnavailablePeriodsQuery';
import { useCacheNetworkQuery } from '@apollo-internal/hooks/useCacheNetworkQuery';
import OpenForBidEventsTable from './OpenForBidEventsTable';
import moment from 'moment';
import { useSetSelectedDate } from '../shared/hooks/useSetSelectedDate';
import AddEventLinkButton from '../shared/components/AddEventLinkButton';

const CHECKED_LABEL = 'ATL';

const AppraiserCalendar = () => {
  const { selectedDate, setSelectedDate } = useSetSelectedDate();

  const {
    data: appraiserCalendarData,
    loading: appraiserCalendarLoading,
    error: appraiserCalendarError
  } = useCacheNetworkQuery(GET_ATL_CALENDAR, {
    variables: {
      selectedYear: moment(selectedDate).year()
    },
    skip: !Boolean(selectedDate)
  });

  const {
    data: unavailablePeriodsData,
    loading: unavailablePeriodsLoading,
    error: unavailablePeriodsError
  } = useCacheNetworkQuery(GET_UNAVAILABLE_PERIODS);

  const [addUnavailablePeriod, { error: addUnavailablePeriodError }] = useMutation(
    ADD_UNAVAILABLE_PERIOD,
    {
      update: (cache, { data }) => {
        addPeriodToCache('unavailablePeriods', cache, data.addUnavailablePeriod.UnavailablePeriod);
      }
    }
  );

  const [deleteUnavailablePeriod, { error: deleteUnavailablePeriodError }] = useMutation(
    DELETE_UNAVAILABLE_PERIOD,
    {
      update: (cache, { data }) => {
        removePeriodFromCache(
          'unavailablePeriods',
          cache,
          data.deleteUnavailablePeriod.DeletedUnavailablePeriod
        );
      }
    }
  );

  const unavailablePeriods = CalendarMapper.toCalendarData(
    unavailablePeriodsData?.unavailablePeriods || []
  );

  const appraiserEvents = CalendarEventMapper.toCalendarEventData(
    appraiserCalendarData?.atlAppraiserEvents || []
  );

  const weeks = getWeekCheckBoxes(selectedDate, unavailablePeriods);

  const addNewUnavailablePeriod = async week => {
    await addUnavailablePeriod({ variables: { period: CalendarMapper.toApolloAddInput(week) } });
  };

  const removeUnavailablePeriod = async week => {
    const unavailablePeriod = unavailablePeriods.find(item => item.start === week.start);
    if (!unavailablePeriod) {
      return;
    }

    await deleteUnavailablePeriod({
      variables: { id: unavailablePeriod.id }
    });
  };

  if (
    appraiserCalendarError ||
    unavailablePeriodsError ||
    addUnavailablePeriodError ||
    deleteUnavailablePeriodError
  ) {
    return <ApolloError />;
  }

  return (
    <div className='calendar-appraiser-view'>
      <div className='add-event-section'>
        <AddEventLinkButton />
      </div>
      <div className='calendar-header'>
        <SkeletonWrapper show={appraiserCalendarLoading || unavailablePeriodsLoading}>
          <MonthlyDatePicker selectedDate={selectedDate} setSelectedDate={setSelectedDate} />
        </SkeletonWrapper>

        <ViewSwitch activeLabel={CHECKED_LABEL} />

        <div className='legends'>
          <span className='appraisal-assigned'>Appraisal Assigned</span>
          <span className='team-review'>Team Review</span>
          <span className='team-assigned'>Team Assigned</span>
          <span className='ready-to-deliver'>Ready to Deliver</span>
        </div>
      </div>
      <SkeletonWrapper
        show={appraiserCalendarLoading || unavailablePeriodsLoading}
        inheritParentSize>
        <div className='calendar-body' data-testid='calendar-body'>
          <WeekCheckboxes
            removeUnavailablePeriod={removeUnavailablePeriod}
            addNewUnavailablePeriod={addNewUnavailablePeriod}
            weeks={weeks}
          />
          <MonthlyCalendar selectedDate={selectedDate} events={appraiserEvents} weeks={weeks} />
        </div>
      </SkeletonWrapper>
      <OpenForBidEventsTable
        openForATLBidEventData={appraiserCalendarData?.openForATLBidEvents || []}
        loading={appraiserCalendarLoading}
      />
    </div>
  );
};

export default AppraiserCalendar;
